import React, {Fragment, useEffect, useRef, useState,} from 'react';

import {ConfigStatus, ConfigType} from '../../types/sensor-configuration';

import DEFAULT_OVERLAYS from './overlays';

import {ConfigV1} from '../../types/config';
import { borderRadiusBase } from '@densityco/ui/variables/spacing.json';

export type ConfigState = {
  sensorSerialNumber: number;
  status: string;
  sensor_serial: string;

  mutable_config: ConfigV1;
  mutable_config_template: ConfigType;
  assigned_config: ConfigV1;
  assigned_config_template: ConfigType;
  config_status: ConfigStatus;

  initialAccuracyPercentage: number | null;
};

export const CaptureViewerVideoContext = React.createContext<HTMLVideoElement | null>(
    null,
);

export type CaptureViewerProps = {
  width?: number;
  height?: number;

  readOnly?: boolean;
  initialStartTimeSeconds?: number;
  autoPlay?: boolean;

  // This overlay is optional because this overlay has a requestanimationframe
  // in it and takes a fair amount of computing power to render.
  showMaxPixelIntensityOverlay?: boolean,

  capture: null | {
    id: number;
    capture_url: string;
    inferences?: {
      [timestamp: number]: Array<{
        bbox: Array<number>,
        centroid: Array<number>,
      }>
    };
  };

  configState: {
    mutable_config: ConfigV1;
    mutable_config_template: ConfigType;
    config_status: ConfigStatus;
  };
  onConfigChange?: (updatedConfig: ConfigV1) => void;

  onVideoRefChanged?: (videoRef: HTMLVideoElement) => void;

  showPoint?: [number, number];
  mpiOverlappingRegionOverlay?: {
    side: 'left' | 'right';
    width: number;
    maxXOffsetMultiplier: number;
  };
};

const CaptureViewer: React.FunctionComponent<CaptureViewerProps> = props => {
  let {
    width,
    height,
    readOnly,
    autoPlay,
    capture,
    configState,
    onConfigChange,
    initialStartTimeSeconds,

    onVideoRefChanged,

    showPoint,
    mpiOverlappingRegionOverlay,
  } = props;

  // This is the spacing on the left and right side of the video that
  // the svg overlaps, so that overlays don't appear to be cut off at the edge
  // of the video.
  const gutterWidthInPx = 16;

  // Apply the "initialStartTimeSeconds" value and update the app
  // whenever the video ref changes.
  const videoRef = useRef<HTMLVideoElement>();
  useEffect(() => {
    if (videoRef.current) {
      if (typeof initialStartTimeSeconds !== 'undefined') {
        videoRef.current.currentTime = initialStartTimeSeconds;
      }
      if (onVideoRefChanged) {
        onVideoRefChanged(videoRef.current);
      }
    }
  }, [videoRef.current]);

  if (width) {
    height = width * 0.75;
  } else if (height) {
    width = height / 0.75;
  } else {
    throw new Error(
        'Either the width prop or the height prop must be set on the capture viewer; neither was found.',
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        width: width + gutterWidthInPx * 2,
        height,
        backgroundColor: 'black',
        paddingLeft: gutterWidthInPx,
        boxSizing: 'border-box',
        borderRadius: borderRadiusBase,
      }}
    >
      {capture ? (
        <Fragment>
          <video
            style={{ position: 'absolute', zIndex: 0 }}
            crossOrigin="anonymous"
            height={height}
            width={width}
            muted
            loop={autoPlay}
            autoPlay={autoPlay}
            src={capture.capture_url}
            ref={videoRef}
            data-test-id="capture-viewer-video"
            onChange={() => {
              if (videoRef.current && onVideoRefChanged) {
                onVideoRefChanged(videoRef.current);
              }
            }}
          />
          <svg
            width={width + gutterWidthInPx * 2}
            height={height}
            // Setting `viewBox` to this value means that the coordinate system within the svg starts at 0
            // and goes to 120 on the x and 160 on the y (the size of the coordinate system as the sensor understands it)
            viewBox={`0 0 160 120`}
            style={{ position: 'absolute', zIndex: 1, left: 0 }}
            data-test-id="capture-viewer-svg"
          >
            {/* Inject the video ref as context so that inner drag handles
                can ufigure out where it is located on the page */}
            <CaptureViewerVideoContext.Provider value={videoRef.current}>
              {DEFAULT_OVERLAYS.map((overlay, index) => {
                if (overlay.isVisible(props)) {
                  const Overlay = overlay.render;
                  return (
                    <Fragment key={index}>
                      <Overlay {...props} />
                    </Fragment>
                  );
                } else {
                  return null;
                }
              })}
            </CaptureViewerVideoContext.Provider>
          </svg>
        </Fragment>
      ) : (
        <h1>Womp</h1>
      )}
    </div>
  );
};

export default CaptureViewer;
